/**
 * email-assets — image asset library for the email designer.
 *
 * Declared-but-unbuilt op family, same seam and honesty convention as
 * queues-api / wf-api: typed wrappers over the `@v0y4ge/api-client` `apiRaw`
 * choke point, served by the preview harness until Claude Code ships the real
 * handlers (REQ-112i). No hand-rolled fetch, no fake success.
 *
 * Why this exists: a dropped file previously became a `data:` URL inline in the
 * email HTML — unsendable (Gmail/Outlook strip embedded images) and it counted
 * toward Gmail's 102KB clip. Uploading returns a STABLE URL instead, so the
 * design references a hosted image exactly as it will in production, and the
 * same image is reusable across messages and languages.
 */
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../../../shell/opSource";

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

export interface EmailAsset {
  id: string;
  /** Stable URL to reference in email HTML. */
  url: string;
  /** PREVIEW ONLY — the image bytes as a data URL.
   *
   *  Why this exists: the preview harness serves `url` by patching `window.fetch`,
   *  but an `<img src>` load does NOT go through fetch — the browser requests it
   *  directly, misses the interceptor and 404s, so every image rendered broken
   *  (operator, 2026-07-31). Until a real backend serves `url` over HTTP, the
   *  designer renders from this instead. A data URL also works inside the
   *  sandboxed preview iframe, which a parent-origin blob URL would not.
   *
   *  When REQ-112i lands, drop this field and use `url` — one substitution in
   *  EmailDesigner (`assetSrc`). */
  previewDataUrl?: string;
  name: string;
  contentType: string;
  bytes: number;
  createdAt: string;
}

/** Upload one image. `dataUrl` is the base64 payload the browser read from the
 *  dropped/picked file; the server (harness today) stores it and returns a URL. */
export const uploadEmailAsset = (input: { name: string; contentType: string; dataUrl: string }) =>
  tag("uploadEmailAsset", apiRaw<EmailAsset>("/v1/admin/assets", { method: "POST", body: JSON.stringify(input) }, 'uploadEmailAsset'));

/** The tenant's recent image assets, newest first. */
export const listEmailAssets = () =>
  tag("listEmailAssets", apiRaw<{ assets: EmailAsset[] }>("/v1/admin/assets"));

export const deleteEmailAsset = (id: string) =>
  tag("deleteEmailAsset", apiRaw<{ deleted: boolean; id: string }>(`/v1/admin/assets/${encodeURIComponent(id)}`, { method: "DELETE" }, 'deleteEmailAsset'));
